import {Controller} from "@hotwired/stimulus"
import {startIcons} from "../import_lucid.js"

// The box at the top of All Reports. In Ask mode the form posts the question to chats#start,
// which opens it as a new PharOS chat. In Search mode it finds what the command palette finds
// — the same /command_palette items, plus live record search where the member may use it —
// in a dropdown under the box. Both modes filter the list below (search#filter, same input).
export default class extends Controller {
    static targets = ["input", "results", "hint", "greeting", "askButton", "searchButton", "askOnly"]
    static values = {
        mode: { type: String, default: "search" },
        itemsUrl: String,
        entitiesUrl: String,
        entitySearch: { type: Boolean, default: false }
    }

    connect() {
        this.activeIndex = -1
        this.greet()
        document.addEventListener("keydown", this.focusOnSlash)
        document.addEventListener("click", this.closeOnOutsideClick)
    }

    disconnect() {
        document.removeEventListener("keydown", this.focusOnSlash)
        document.removeEventListener("click", this.closeOnOutsideClick)
        clearTimeout(this.entityTimer)
    }

    ask() {
        this.modeValue = "ask"
        this.inputTarget.focus()
    }

    search() {
        this.modeValue = "search"
        this.inputTarget.focus()
    }

    modeValueChanged() {
        const asking = this.modeValue === "ask"
        this.inputTarget.placeholder = this.inputTarget.dataset[asking ? "askPlaceholder" : "searchPlaceholder"] || this.inputTarget.placeholder
        this.paintSwitch(this.askButtonTargets, asking, "text-brand")
        this.paintSwitch(this.searchButtonTargets, !asking, "text-ink")
        this.askOnlyTargets.forEach(el => el.classList.toggle("hidden", !asking))
        if (this.hasHintTarget) {
            this.hintTarget.innerHTML = asking
                ? 'Press <b class="font-medium text-brand">Enter</b> to ask PharOS. Reports and apps below filter as you type.'
                : 'Finds reports, pages, apps and settings, like the command palette. <b class="font-medium text-ink">↑ ↓</b> to move, <b class="font-medium text-ink">Enter</b> to open.'
        }
        this.typed()
    }

    paintSwitch(buttons, on, onText) {
        buttons.forEach(button => {
            button.setAttribute("aria-pressed", on)
            button.classList.toggle("bg-surface", on)
            button.classList.toggle("shadow-sm", on)
            button.classList.toggle(onText, on)
            button.classList.toggle("text-ink-muted", !on)
        })
    }

    // A starter question goes into the box to be sent or edited, as on the PharOS page.
    fill(event) {
        this.modeValue = "ask"
        this.inputTarget.value = event.currentTarget.dataset.question
        this.inputTarget.focus()
    }

    // Ask posts (a question, or files alone, as in the chat composer); Search never does —
    // Enter there opens the highlighted result instead.
    submit(event) {
        const attached = this.element.querySelector('input[name="upload_ids[]"]')
        if (this.modeValue === "ask" && (this.query || attached)) return
        event.preventDefault()
    }

    typed() {
        const query = this.query.toLowerCase()
        if (this.modeValue !== "search" || !query) return this.close()

        this.loadItems().then(() => {
            if (this.query.toLowerCase() !== query) return
            this.render(query)
            if (query.length >= 3 && this.entitySearchValue) this.searchRecords(query)
        })
    }

    keydown(event) {
        if (this.modeValue !== "search") return
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault()
                this.move(1)
                break
            case "ArrowUp":
                event.preventDefault()
                this.move(-1)
                break
            case "Enter": {
                event.preventDefault()
                const link = this.rows()[this.activeIndex]
                if (!link) break
                if (event.metaKey || event.ctrlKey) window.open(link.href, "_blank")
                else link.click()
                break
            }
            case "Escape":
                this.close()
                break
        }
    }

    get query() {
        return this.inputTarget.value.trim()
    }

    // Fetched once per page view, on the first search — the same list the palette fetches.
    loadItems() {
        this.itemsRequest ||= fetch(this.itemsUrlValue, { headers: { Accept: "application/json" } })
            .then(response => response.ok ? response.json() : [])
            .catch(() => [])
            .then(items => { this.items = items })
        return this.itemsRequest
    }

    render(query) {
        const matches = (this.items || []).filter(item => item.name.toLowerCase().includes(query))
        const sections = matches.reduce((acc, item) => {
            (acc[item.section] ||= []).push(item)
            return acc
        }, {})
        const html = Object.entries(sections).map(([section, items]) =>
            this.sectionHtml(section) + items.map(item => this.rowHtml(item.path, item.name, item.icon, item.colour)).join("")
        ).join("")
        this.resultsTarget.innerHTML = html || `<div data-no-results class="px-3 py-6 text-center text-meta text-ink-muted">No results for “${this.escape(this.query)}”</div>`
        this.open()
        this.select(0)
    }

    // Records (data organizations with :search_entities), as the palette lists them.
    searchRecords(query) {
        clearTimeout(this.entityTimer)
        this.entityTimer = setTimeout(async () => {
            try {
                const response = await fetch(`${this.entitiesUrlValue}?q=${encodeURIComponent(query)}`, { headers: { Accept: "application/json" } })
                if (!response.ok || this.query.toLowerCase() !== query) return
                const links = (await response.json()).flatMap(result =>
                    (result.report_links || []).map(link => ({ path: link.url, name: `${result.display} — ${link.label}` }))
                )
                if (!links.length) return
                this.resultsTarget.querySelector("[data-no-results]")?.remove()
                this.resultsTarget.insertAdjacentHTML("beforeend",
                    this.sectionHtml("Records") + links.map(link => this.rowHtml(link.path, link.name, "bar-chart", "#6366f1")).join(""))
                startIcons(this.resultsTarget)
                if (this.activeIndex < 0) this.select(0)
            } catch (_error) {
                // Record search is a bonus on top of the palette items; a failure leaves those.
            }
        }, 300)
    }

    sectionHtml(section) {
        return `<div class="px-2.5 pt-2 pb-1 text-eyebrow font-semibold uppercase text-ink-faint">${this.escape(section)}</div>`
    }

    rowHtml(path, name, icon, colour) {
        const tint = this.escape(colour || "#6366f1")
        return `<a href="${this.escape(path)}" data-result data-turbo-prefetch="false" role="option"
                   class="flex items-center gap-3 px-2.5 py-1.5 rounded-card text-ui text-ink hover:bg-surface-subtle"
                   data-action="mouseenter->home-search#hover">
                  <span class="w-7 h-7 rounded-card grid place-items-center shrink-0" style="background-color: ${tint}1a;">
                    <i data-lucide="${this.escape(icon || "file-text")}" class="w-4 h-4" style="color: ${tint};"></i>
                  </span>
                  <span class="truncate">${this.escape(name)}</span>
                </a>`
    }

    rows() {
        return Array.from(this.resultsTarget.querySelectorAll("[data-result]"))
    }

    hover(event) {
        this.select(this.rows().indexOf(event.currentTarget))
    }

    move(step) {
        const rows = this.rows()
        if (!rows.length) return
        this.select((this.activeIndex + step + rows.length) % rows.length)
        rows[this.activeIndex].scrollIntoView({ block: "nearest" })
    }

    select(index) {
        const rows = this.rows()
        rows.forEach((row, i) => row.classList.toggle("bg-brand-soft", i === index))
        this.activeIndex = rows[index] ? index : -1
    }

    open() {
        this.resultsTarget.classList.remove("hidden")
        startIcons(this.resultsTarget)
    }

    close() {
        this.resultsTarget.classList.add("hidden")
        this.activeIndex = -1
    }

    closeOnOutsideClick = (event) => {
        if (!this.element.contains(event.target)) this.close()
    }

    // "/" jumps to the box from anywhere on the page that is not already taking typing.
    focusOnSlash = (event) => {
        if (event.key !== "/" || event.metaKey || event.ctrlKey) return
        if (event.target.closest("input, textarea, select, [contenteditable]")) return
        event.preventDefault()
        this.inputTarget.focus()
    }

    // The server does not know the member's time zone; the browser does.
    greet() {
        if (!this.hasGreetingTarget) return
        const hour = new Date().getHours()
        let part = "evening"
        if (hour < 12) part = "morning"
        else if (hour < 17) part = "afternoon"
        this.greetingTarget.textContent = `Good ${part}, ${this.greetingTarget.dataset.name}`
    }

    escape(value) {
        const div = document.createElement("div")
        div.textContent = String(value ?? "")
        return div.innerHTML.replace(/"/g, "&quot;")
    }
}
